import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import type { BulkUpdateSelection } from '~/api/civilsearch';
import type { DataRequestV2, Outcome, RequestStatus, UtilityPresence } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useBulkUpdateDataRequests } from '~/hooks/civilsearch/useDataRequest';

export type FollowUpFields = {
  requestStatus?: RequestStatus;
  firstContactedAt?: string;
  secondContactedAt?: string;
  thirdContactedAt?: string;
  fourthContactedAt?: string;
  calledDate?: string;
  nextFollowUpAt?: string;
  outcome?: Outcome;
  utilityPresence?: UtilityPresence;
  resolutionDate?: string;
  notesInternal?: string;
};

const DATE_FIELD_KEYS = new Set<keyof FollowUpFields>([
  'firstContactedAt',
  'secondContactedAt',
  'thirdContactedAt',
  'fourthContactedAt',
  'calledDate',
  'nextFollowUpAt',
  'resolutionDate',
]);

export type UseBulkEditFollowUpParams = {
  buildSearchPayloadForBulk: () => { [key: string]: any };
  getActiveQueryKeyParams: () => object;
};

export function useBulkEditFollowUp({ buildSearchPayloadForBulk, getActiveQueryKeyParams }: UseBulkEditFollowUpParams) {
  const queryClient = useQueryClient();
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [fields, setFields] = useState<FollowUpFields>({});
  const mutation = useBulkUpdateDataRequests();

  const open = useCallback((selection: ServerSideSelectionState) => {
    setPendingSelection(selection);
    setFields({});
  }, []);

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const setField = useCallback(<K extends keyof FollowUpFields>(key: K, value: FollowUpFields[K] | null) => {
    setFields((prev) => {
      if (value == null) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const hasChanges = Object.keys(fields).length > 0;

  const onSubmit = useCallback(() => {
    if (!pendingSelection || !hasChanges) return;

    const selection: BulkUpdateSelection =
      pendingSelection.mode === 'all' ? { ...pendingSelection, search: buildSearchPayloadForBulk() } : pendingSelection;

    // Capture the exact query key at submit time so the cache patch targets
    // only the currently-visible variant, not all cached filter/page variants.
    const activeQueryKey = ['data-requests-v2', getActiveQueryKeyParams()];

    // Build the cache patch: date strings become DateTime objects to match the
    // shape that transformDataRequest produces
    const { notesInternal, ...nonNoteFields } = fields;
    const cachePatch: Partial<DataRequestV2> = Object.fromEntries(
      Object.entries(nonNoteFields).map(([key, value]) => [
        key,
        DATE_FIELD_KEYS.has(key as keyof FollowUpFields) && typeof value === 'string' ? DateTime.fromISO(value) : value,
      ]),
    ) as Partial<DataRequestV2>;

    mutation.mutate(
      { selection, updates: fields },
      {
        onSuccess: () => {
          setPendingSelection(null);
          queryClient.setQueryData<{ requests: DataRequestV2[] }>(activeQueryKey, (old) => {
            if (!old) return old;
            return {
              ...old,
              requests: old.requests.map((row) => {
                const affected =
                  selection.mode === 'partial'
                    ? selection.selectedIds.includes(row.id)
                    : !selection.excludedIds.includes(row.id);
                if (!affected) return row;
                const updatedRow = { ...row, ...cachePatch };
                if (notesInternal !== undefined) {
                  const existing = row.notesInternal || '';
                  updatedRow.notesInternal = existing ? `${existing}\n\n${notesInternal}` : notesInternal;
                }
                return updatedRow;
              }),
            };
          });
        },
        onError: () => {
          Notify.error({ message: 'Failed to update status. Please try again.' });
        },
      },
    );
  }, [pendingSelection, fields, hasChanges, buildSearchPayloadForBulk, getActiveQueryKeyParams, mutation, queryClient]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    fields,
    setField,
    hasChanges,
    onSubmit,
    isPending: mutation.isPending,
  };
}
